import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { GlobalNavbar } from '../components/global-navbar';
import { blogPosts } from '../content/blogPosts';
import { useSeo } from '../utils/useSeo';

export function BlogIndexPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useSeo({
    description:
      'Read Profit Cars insights for EU car dealers: sourcing strategy, AI listing workflows, and margin optimization.',
    image: '/car-dealer-hero.png',
    keywords: [
      'car dealer blog',
      'used car dealership insights',
      'car flipping tips',
      'dealer sourcing strategy',
      'dealer margin optimization',
    ],
    path: '/blog',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        description:
          'Profit Cars insights for car dealers on sourcing, listing quality, and finance management.',
        name: 'Profit Cars Blog',
        url: '/blog',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: blogPosts.map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `/blog/${post.slug}`,
        })),
        name: 'Profit Cars Blog Posts',
      },
    ],
    title: 'Profit Cars Blog | Insights for Car Dealers',
    type: 'website',
  });

  return (
    <main className="min-h-screen bg-[#ececec] text-[#111827]">
      <GlobalNavbar />

      <section className="border-b border-black/10 bg-[#e6e7ea]">
        <div className="mx-auto w-full max-w-6xl px-6 pb-12 pt-12 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2f5b1b]">Profit Cars Insights</p>
            <h1 className="mt-4 font-display text-5xl leading-[0.95] text-black sm:text-6xl">
              insights every car dealer should know
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg">
              Practical articles about deal sourcing, listing quality, and margin control for modern car dealers.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {blogPosts.map((post) => (
              <Link
                className="group block overflow-hidden rounded-[24px] border border-black/10 bg-white transition hover:border-black/25"
                key={post.slug}
                to={`/blog/${post.slug}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-black/5">
                  <img
                    alt={post.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                    src={post.image}
                  />
                </div>

                <div className="p-5">
                  <h2 className="text-xl font-extrabold leading-tight text-black">{post.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{post.description}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.09em] text-[#2f5b1b]">
                    Read article
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
